import React, {useState} from 'react';

import {
  usePublicEventQuery,
  useCurrentUserQuery,
  useFollowMutation,
  useUnfollowMutation,
} from '@/api';

import {AddressMapView} from '../components/AddressMapView';
import {OutlinePrimaryLink} from '../components/OutlinePrimaryLink';
import {SafeHtml} from '../components/SafeHtml';

const getEventId = (): string =>
  document.getElementById('public-root')?.dataset.eventId ?? '';

const MetaItem: React.FC<{icon: string; children: React.ReactNode}> = ({
  icon,
  children,
}) => (
  <div className="mb-5 flex items-start text-sm">
    <i
      className={`${icon} mt-0.5 w-5 shrink-0 text-lg text-primary`}
      aria-hidden="true"
    />
    <div className="ml-4 leading-relaxed">{children}</div>
  </div>
);

const MetaLabelItem: React.FC<{label: string; children: React.ReactNode}> = ({
  label,
  children,
}) => (
  <div className="mb-5 flex text-sm">
    <div className="mr-5 shrink-0 whitespace-nowrap text-gray-700">{label}</div>
    <div className="leading-relaxed">{children}</div>
  </div>
);

type GroupProps = {
  id?: string | null;
  name?: string | null;
  mainImageUrl?: string | null;
  groupFollowerCount?: number | null;
  isFollower?: boolean | null;
};

// フォロー中かは publicEvent の group.isFollower で判定する。myFollows は未ログインだと Login required になり、
// apolloClient がログイン画面へ移すので呼ばない（#246）
const EventGroupCard: React.FC<{group: GroupProps}> = ({group}) => {
  const {data: {currentUser = null} = {}} = useCurrentUserQuery();
  const [isFollowing, setIsFollowing] = useState(group.isFollower ?? false);
  const [followerCount, setFollowerCount] = useState(
    group.groupFollowerCount ?? 0,
  );
  const [follow] = useFollowMutation({refetchQueries: ['myFollows']});
  const [unfollow] = useUnfollowMutation({refetchQueries: ['myFollows']});

  const handleToggle = async () => {
    if (!isFollowing) {
      try {
        const result = await follow({variables: {groupId: group.id ?? ''}});
        const count = result.data?.follow?.group?.groupFollowerCount;
        setIsFollowing(true);
        setFollowerCount(count ?? followerCount + 1);
      } catch {
        // mutation may fail when unauthenticated — leave UI state unchanged
      }
    } else {
      try {
        const result = await unfollow({variables: {groupId: group.id ?? ''}});
        const count = result.data?.unfollow?.group?.groupFollowerCount;
        setIsFollowing(false);
        setFollowerCount(count ?? Math.max(0, followerCount - 1));
      } catch {
        // mutation may fail when unauthenticated — leave UI state unchanged
      }
    }
  };

  return (
    <div className="mt-6 border-t border-neutral-100 pt-5">
      <div className="mb-4 inline-flex border-b-2 border-primary pb-1.5 text-sm tracking-wide text-primary">
        主催グループ
      </div>
      <div className="flex items-start gap-4">
        <a href={`/groups/${group.id}`} className="shrink-0">
          <img
            src={group.mainImageUrl ?? '/assets/noimage.png'}
            alt={group.name ?? ''}
            className="h-[100px] w-[100px] object-cover"
          />
        </a>
        <div className="min-w-0">
          <a
            href={`/groups/${group.id}`}
            className="block text-sm font-semibold text-neutral-900 hover:text-primary">
            {group.name}
          </a>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-xs text-neutral-500">
              フォロワー{' '}
              <span className="font-bold text-primary">{followerCount}</span>人
            </span>
            {currentUser ? (
              <OutlinePrimaryLink
                href="#"
                variant={isFollowing ? 'outline' : 'filled'}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  void handleToggle();
                }}>
                {isFollowing ? 'フォロー中' : 'フォローする'}
              </OutlinePrimaryLink>
            ) : (
              <OutlinePrimaryLink
                href={`/signin?return_url=${window.location.href}`}
                variant="filled"
                size="sm">
                フォローする
              </OutlinePrimaryLink>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const EventDetailPage: React.FC = () => {
  const id = getEventId();
  const {data, loading} = usePublicEventQuery({
    variables: {id},
    skip: !id,
  });
  const event = data?.publicEvent;

  if (loading) return <main className="p-8 text-gray-500">読み込み中...</main>;
  if (!event)
    return (
      <main className="p-8 text-gray-500">イベントが見つかりません。</main>
    );

  return (
    <main className="mx-auto max-w-screen-xl px-4 pb-16 pt-[72px]">
      {/* Hero: image left + title/CTA right */}
      <section className="flex flex-col border-b border-black/50 pb-24 md:flex-row md:items-start">
        <div className="w-full md:mr-20 md:w-[63%]">
          <img
            src={event.mainImageUrl ?? '/assets/Noimage-EventsEvent1.png'}
            alt={event.title ?? ''}
            className="w-full"
          />
        </div>
        <div className="mt-6 w-full md:mt-10 md:flex-1">
          {event.eventSchedules && event.eventSchedules.length > 0 && (
            <div className="mb-3 flex items-start gap-2.5 text-primary">
              <i className="far fa-clock mt-0.5 text-base" aria-hidden="true" />
              <div>
                {event.eventSchedules.map((s) =>
                  s ? (
                    <p key={s.id} className="mb-1 text-sm">
                      {s.eventDateText}
                      {s.eventDateWday && `（${s.eventDateWday}）`}
                      {s.startTimeText && ` ${s.startTimeText}`}
                    </p>
                  ) : null,
                )}
              </div>
            </div>
          )}
          <h1 className="mb-4 text-2xl leading-snug text-gray-800">
            {event.title}
          </h1>
          {event.organizer && (
            <p className="mb-6 mt-4 text-sm">
              主催 <span className="text-primary">{event.organizer}</span>
            </p>
          )}
          {event.canOrder ? (
            <OutlinePrimaryLink
              href={`/events/${event.id}/apply`}
              variant="filled">
              チケットを申し込む
            </OutlinePrimaryLink>
          ) : (
            <OutlinePrimaryLink
              href="#"
              variant="filled"
              className="pointer-events-none opacity-60">
              満席です
            </OutlinePrimaryLink>
          )}
        </div>
      </section>

      {/* Body: description left + meta right */}
      <div className="mt-16 flex flex-col md:flex-row">
        <div className="md:w-[60%] md:pr-5">
          <div className="mb-10">
            <span className="inline-flex border-b-[3px] border-primary pb-3 text-base tracking-wide text-primary">
              詳細情報
            </span>
          </div>
          {event.description ? (
            <SafeHtml
              html={event.description}
              className="text-sm leading-relaxed text-gray-700 md:text-base md:leading-loose"
            />
          ) : (
            <p className="text-sm text-gray-500">詳細情報はありません。</p>
          )}
        </div>
        <div className="mt-12 md:mt-0 md:w-[40%]">
          {event.eventSchedules && event.eventSchedules.length > 0 && (
            <MetaItem icon="far fa-clock">
              <div>
                {event.eventSchedules.map((s) =>
                  s ? (
                    <div key={s.id} className="mb-0.5">
                      {s.eventDateText}
                      {s.eventDateWday && `（${s.eventDateWday}）`}
                      {s.startTimeText && ` ${s.startTimeText}`}
                      {s.endTimeText && ` 〜 ${s.endTimeText}`}
                    </div>
                  ) : null,
                )}
              </div>
            </MetaItem>
          )}
          {event.dueDateTextFull && (
            <MetaItem icon="far fa-clock">
              <p>チケット申込期限</p>
              <p>
                {event.dueDateTextFull} {event.dueTimeText}
              </p>
            </MetaItem>
          )}
          {event.cancelDateTextFull && (
            <MetaItem icon="far fa-clock">
              <p>申込キャンセル期限</p>
              <p>
                {event.cancelDateTextFull} {event.cancelTimeText}
              </p>
            </MetaItem>
          )}
          <MetaItem icon="fas fa-yen-sign">
            {event.isFree ? (
              <p>無料</p>
            ) : (
              <div>
                <p>¥{event.ticketAmount?.toLocaleString()}（税込）</p>
                {event.ticketPaymentMethodText && (
                  <p className="mt-1 text-xs text-gray-500">
                    ※ {event.ticketPaymentMethodText}
                  </p>
                )}
              </div>
            )}
          </MetaItem>
          {(event.location || event.address) && (
            <MetaItem icon="fas fa-map-marker">
              <div>
                {event.location && (
                  <p>
                    {event.locationType === 'acm' ? 'ACミハラ' : event.location}
                  </p>
                )}
                {event.address && <p>{event.address}</p>}
              </div>
            </MetaItem>
          )}
          {event.address && (
            <div className="mb-5 h-[220px] w-full">
              <AddressMapView address={event.address} />
            </div>
          )}
          {event.organizer && (
            <MetaLabelItem label="主催">{event.organizer}</MetaLabelItem>
          )}
          {(event.informationEmail || event.informationTel) && (
            <MetaLabelItem label="問い合わせ先">
              {event.informationEmail && <p>{event.informationEmail}</p>}
              {event.informationTel && <p>{event.informationTel}</p>}
            </MetaLabelItem>
          )}
          {event.group && <EventGroupCard group={event.group} />}
        </div>
      </div>
    </main>
  );
};
